import { useCallback } from 'react'
import { ProductOpeningHoursResponse } from '@leid/api-locations'
import { ProductAvailabilityInfo } from '@leid/api-orders'
import { endOfHour, isBefore, startOfHour } from 'date-fns'

import { CartFormValues } from 'appTypes/orders'
import { DateTimePickerHourPredicate } from 'components/form/DateTimePicker/interfaces'
import {
  checkIfPeriodStartsEndsInWorkingHours,
  getAvailabilitiesChanges,
  getLocationAvailabilityInfo,
} from 'helpers/utils/availabilities'

export const useShouldDisablePickupHour = (
  isProlonging: boolean,
  values: CartFormValues,
  productLocationAvailability: ProductAvailabilityInfo,
  openingHoursInfo: ProductOpeningHoursResponse[],
): DateTimePickerHourPredicate => {
  return useCallback(
    (hour, date) => {
      if (isProlonging) {
        return true
      }

      const now = startOfHour(new Date())
      if (isBefore(date, now)) {
        return true
      }

      if (!values.selectedLocationId) {
        // No location selected - show all hours as available
        return false
      }

      const locationAvailability = getLocationAvailabilityInfo(values.selectedLocationId, productLocationAvailability)
      if (!locationAvailability) {
        // no info for location — not available
        return true
      }

      const start = startOfHour(date)
      const end = endOfHour(start)

      const availabilitiesChange = getAvailabilitiesChanges(start, end, locationAvailability, false)
      if (availabilitiesChange.some((s) => s < values.amount)) {
        // Hour is not available
        return true
      }

      // Check opening hours for start and end of period
      const startsOrEndsInWorkingHours = checkIfPeriodStartsEndsInWorkingHours(
        start,
        end,
        values.selectedLocationId,
        true, // Check only that it's start time
        openingHoursInfo,
        true, // Here check time
      )
      if (!startsOrEndsInWorkingHours) {
        // It's not working hours or day
        return true
      }

      return false
    },
    [
      isProlonging,
      values.selectedLocationId,
      values.selectedRegionId,
      productLocationAvailability,
      openingHoursInfo,
      values.amount,
    ],
  )
}
